import { z } from 'zod'

// Which registration path the person picked on the sign-in screen. SignIn
// unmounts the instant a session lands (App.tsx swaps it for Onboarding), so
// the choice can't live in React state — it has to survive that hop, and a
// page reload part-way through. sessionStorage, not localStorage: the choice
// is meaningless once the tab is gone.
const KEY = 'hewey.auth.intent'
const NAME_KEY = 'hewey.auth.suggestedName'

export const SignupIntent = z.enum(['create', 'join'])
export type SignupIntent = z.infer<typeof SignupIntent>

export const setSignupIntent = (intent: SignupIntent): void => {
	try {
		window.sessionStorage.setItem(KEY, intent)
	} catch {
		// storage disabled — Onboarding falls back to asking outright
	}
}

// A pure read, so it's safe in a useState initializer (StrictMode runs those
// twice in dev). Call clearSignupIntent once it's been acted on.
export const readSignupIntent = (): SignupIntent | null => {
	let raw: string | null
	try {
		raw = window.sessionStorage.getItem(KEY)
	} catch {
		return null
	}
	if (raw === null) return null
	const parsed = SignupIntent.safeParse(raw)
	return parsed.success ? parsed.data : null
}

export const clearSignupIntent = (): void => {
	try {
		window.sessionStorage.removeItem(KEY)
		window.sessionStorage.removeItem(NAME_KEY)
	} catch {
		// storage disabled — nothing to clear
	}
}

// The first name Apple hands over, only ever on someone's first sign-in.
// Onboarding offers it as their display name. Same hop as the intent above,
// and cleared with it.
export const setSuggestedName = (name: string | null): void => {
	try {
		if (name) window.sessionStorage.setItem(NAME_KEY, name)
		else window.sessionStorage.removeItem(NAME_KEY)
	} catch {
		// storage disabled — Onboarding starts with an empty name
	}
}

export const readSuggestedName = (): string | null => {
	try {
		return window.sessionStorage.getItem(NAME_KEY)
	} catch {
		return null
	}
}
